import countryTelephonePrefix from "./country-telephone-prefix";
import { FLAG_NAME } from "./langs/flag-name";
import { nativeLangMap } from "./language";
import { FLAG_BASE_URL } from "./constant";
import { ARRAY_SUBTITLE_LANGUAGE } from "./langs/array-subtitle-language";
import { CDN_BASE_URL } from "./constant";
/**
 * @description 参数序列化
 * @param {Object} obj 对象
 * @returns {String} 序列化后的字符串
 * @example import { objToQuerystring } from "@ht/utils";
 *
 * objToQuerystring({a:1,b:3,c:[1,2,3]})
 * // 输出'a=1&b=3&c=1&c=2&c=3'
 *
 * objToQuerystring({a:1,d:2,c:{d:5}})
 * // 输出'a=1&d=2&c={"d":5}'
 */
export const objToQuerystring = (obj: Record<string, unknown>) => {
  const _result = [];
  for (const key in obj) {
    const value = obj[key];
    if (Array.isArray(value)) {
      value.forEach(_value => {
        _result.push(key + "=" + _value);
      });
    } else if (Object.prototype.toString.call(value) === `[object Object]`) {
      _result.push(key + "=" + JSON.stringify(value));
    } else {
      _result.push(key + "=" + value);
    }
  }
  return _result.join("&");
};

/**
 * @description 通过用户国籍获取号码前缀
 * @param {String} nationality 用户国籍
 * @returns {Object} 号码前缀对象 code,country,country_code
 * @example import { getCountryPhoneCode } from "@ht/utils";
 * getCountryPhoneCode('CN')
 */
export const getCountryPhoneCode = (nationality: string) => {
  const result = countryTelephonePrefix.find(
    item => item.country_code === nationality
  );
  const have = {
    code: "",
    country: "",
    country_code: ""
  };
  return result ? result : have;
};

/**
 *
 * @description 按照key对象排序
 * @param {Object} obj 对象
 * @returns {Object} 排序后的对象
 * @example import { sortObj } from "@ht/utils";
 * sortObj({a:1,b:3,c:2})
 */
export const sortObj = (obj: Record<string, unknown>) => {
  const keysArr = Object.keys(obj).sort();
  const sortObj = {} as Record<string, unknown>;
  for (const i in keysArr) {
    sortObj[keysArr[i]] = obj[keysArr[i]];
  }
  return sortObj;
};

/**
 * @description 通过用户国籍获取国旗URL
 * @param {String} params 用户国籍
 * @returns {String} 国旗URL
 * @example import { getFlagUrl } from "@ht/utils";
 * getFlagUrl('CN')
 *
 */
export const getFlagUrl = (params: string): string => {
  let url = "";
  if (params === "HT_OFFICIAL") {
    return "https://ali-global-cdn.hellotalk8.com/pub/flags/HT_OFFICIAL@2x.png?v=1";
  }
  if (!/^[A-Z]+$/.test(params)) {
    return "";
  }
  FLAG_NAME.source.some(ele => {
    if (ele["code"] === params) {
      url = FLAG_BASE_URL + ele["en"] + "@2x.png";
      return true;
    }
  });
  return `${url}?v=1`;
};

/**
 * @description 根据【number】语言，获取国籍简称
 * @param {Number} lang
 * @returns {String} 国籍简称
 * @example import { formatNationaltyCode } from "@ht/utils";
 * formatNationaltyCode(1)
 */
export const formatNationaltyCode = (lang: number): string => {
  let shorter = "";
  const Lang = nativeLangMap[lang as keyof typeof nativeLangMap];
  ARRAY_SUBTITLE_LANGUAGE.forEach(item => {
    if (item.fullName === Lang) {
      shorter = item.shorter;
    }
  });
  if ([2, 3, 4].includes(lang)) {
    shorter = "CN";
  }
  return shorter;
};

/**
 * 格式化客户端版本号为inter
 * @param {String | Number} version 版本号
 * @returns {Number} 格式化后的版本号
 * @example import { formatVersion } from "@ht/utils";
 * formatVersion("1.0.0")
 * // 返回10000
 */
export const formatVersion = (version: string | number): number => {
  let result: number;
  let ver = "";
  if (typeof version === "number") {
    ver = version.toString();
  } else {
    ver = version;
  }
  if (ver.indexOf(".") === -1) {
    result = +ver * 10000;
  } else {
    const arr = ver.split(".");
    if (arr.length === 2) {
      result = +arr[0] * 10000 + +arr[1] * 100;
    } else {
      result = +arr[0] * 10000 + +arr[1] * 100 + +arr[2];
    }
  }
  return result;
};

/**
 * 判断客户端版本号是否大于等于某个版本号
 * @param {String | Number} limitVersion 限制版本号
 * @param {String | Number} currentVersion 当前版本号
 * @returns {Boolean} 是否大于等于
 * @example import { canIuseVersion } from "@ht/utils";
 * canIuseVersion("1.0.0", "1.0.1")
 */
export const canIuseVersion = (
  limitVersion: string | number,
  currentVersion?: string | number
): boolean => {
  let currentVerStr;
  if (currentVersion) {
    currentVerStr = currentVersion;
  } else if (globalThis.localStorage) {
    const appUserInfo = localStorage.getItem("appUserInfo")
      ? JSON.parse(localStorage.getItem("appUserInfo") as string)
      : null;
    if (appUserInfo) {
      const { version } = appUserInfo;
      currentVerStr = version;
    }
  }
  if (!currentVerStr) return false;

  const currentVer = formatVersion(currentVerStr);
  const compareVer = formatVersion(limitVersion);

  let flag = true;
  if (currentVer < compareVer) {
    flag = false;
  }
  return flag;
};

/**
 * 获取url问号的参数
 * @param {String} url 地址
 * @param {String} name 问号的参数
 * @returns {String} 参数值
 * @example import { getUrlKey } from "@ht/utils";
 * getUrlKey("http://www.baidu.com?a=1&b=2", "a")
 * // 返回1
 *
 */
export const getUrlKey = (url: string, name: string) => {
  return (
    decodeURIComponent(
      (new RegExp("[?|&]" + name + "=" + "([^&;]+?)(&|#|;|$)").exec(url) || [
        "",
        ""
      ])[1].replace(/\+/g, "%20")
    ) || null
  );
};

/**
 * 确保url包含协议
 * @param {String} url 地址
 * @returns {String} 包含协议的地址
 * @example import { ensureProtocol } from "@ht/utils";
 *
 * ensureProtocol("//ali-global-cdn.hellotalk8.com/h5_common_images/avatar.png") => 返回//ali-global-cdn.hellotalk8.com/h5_common_images/avatar.png
 *
 * ensureProtocol("https://ali-global-cdn.hellotalk8.com/h5_common_images/avatar.png") => 返回 https://ali-global-cdn.hellotalk8.com/h5_common_images/avatar.png
 *
 * ensureProtocol("http://ali-global-cdn.hellotalk8.com/h5_common_images/avatar.png") => 返回 ensureProtocol("http://ali-global-cdn.hellotalk8.com/h5_common_images/avatar.png")
 *
 * ensureProtocol("ali-global-cdn.hellotalk8.com/h5_common_images/avatar.png") => 返回ali-global-cdn.hellotalk8.com/h5_common_images/avatar.png
 *
 * ensureProtocol("/h5_common_images/avatar.png") => 返回 https://ali-global-cdn.hellotalk8.com/h5_common_images/avatar.png
 *
 * ensureProtocol("h5_common_images/avatar.png") => 返回 https://ali-global-cdn.hellotalk8.com/h5_common_images/avatar.png
 */

export const ensureProtocol = (url: string | undefined): string => {
  if (!url) return "";
  // 如果url已经包含协议，则直接返回
  if (
    /^(http:\/\/|https:\/\/)|(^(?!http:\/\/|https:\/\/).*\.hellotalk8\.com)/.test(
      url
    )
  ) {
    return url;
  }

  return url.startsWith("/")
    ? `${CDN_BASE_URL}${url}`
    : `${CDN_BASE_URL}/${url}`;
};
